import { jsPDF } from 'jspdf';
import type { CassemsCartLine, CassemsCartTotals } from '../contexts/CassemsCartContext';
import type { CassemsPreferenciaPagamento } from './cassemsWhatsappPedido';

type Cliente = { nome: string; telefone: string; cpf: string; endereco: string; setor: string };

/** Paleta alinhada ao storefront (tailwind lunins.*) */
const C = {
  night: { r: 7, g: 11, b: 20 },
  deep: { r: 12, g: 18, b: 34 },
  accent: { r: 45, g: 212, b: 191 },
  gold: { r: 212, g: 165, b: 116 },
  muted: { r: 100, g: 110, b: 125 },
  line: { r: 220, g: 226, b: 234 },
  softBg: { r: 248, g: 250, b: 252 },
};

const PAGE_W = 210;
const M = 16;
const CONTENT_W = PAGE_W - 2 * M;
const FOOTER_Y = 282;

function fmtBRL(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function refPedido(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `LNS-CMS-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

function slugifyFilePart(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function linhaPreferenciaPagamento(_p: CassemsPreferenciaPagamento): string {
  return 'Pagamento a combinar com a equipe Lunins (horários flexíveis, inclusive na entrega). Sem cobrança online automática nesta campanha.';
}

function drawPageHeader(doc: jsPDF, yStart: number, isContinuation: boolean): number {
  let y = yStart;
  doc.setFillColor(C.night.r, C.night.g, C.night.b);
  doc.rect(0, 0, PAGE_W, isContinuation ? 14 : 34, 'F');
  doc.setDrawColor(C.gold.r, C.gold.g, C.gold.b);
  doc.setLineWidth(0.4);
  doc.line(0, isContinuation ? 14 : 34, PAGE_W, isContinuation ? 14 : 34);

  doc.setTextColor(255, 255, 255);
  if (!isContinuation) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text('L.D.Z ARQUITETURA BEM ESTAR LTDA  ·  CNPJ 63.105.156/0001-57', M, 9);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('LUNINS', M, 19);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(C.accent.r, C.accent.g, C.accent.b);
    doc.text('Comprovante de encomenda  ·  Campanha Cassems (ação de bem-estar)', M, 25);
    doc.setTextColor(220, 225, 232);
    doc.setFontSize(7.5);
    doc.text('Documento para conferência do cliente. NF-e e cobrança seguem confirmação com a equipe.', M, 30);
    y = 40;
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Lunins — Comprovante de encomenda (continuação)', M, 9);
    y = 20;
  }
  doc.setTextColor(0, 0, 0);
  return y;
}

function ensureSpace(doc: jsPDF, y: number, needMm: number): number {
  if (y + needMm > FOOTER_Y - 8) {
    doc.addPage();
    return drawPageHeader(doc, 0, true);
  }
  return y;
}

export function buildCassemsPedidoPdf(params: {
  cliente: Cliente;
  lines: CassemsCartLine[];
  totais: CassemsCartTotals;
  preferenciaPagamento: CassemsPreferenciaPagamento;
}) {
  const { cliente, lines, totais, preferenciaPagamento } = params;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const emitido = new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  const ref = refPedido();

  let y = drawPageHeader(doc, 0, false);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(C.muted.r, C.muted.g, C.muted.b);
  doc.text(`Referência do pedido: ${ref}`, M, y);
  doc.text(`Emitido em: ${emitido}`, PAGE_W - M, y, { align: 'right' });
  y += 8;
  doc.setDrawColor(C.line.r, C.line.g, C.line.b);
  doc.line(M, y, PAGE_W - M, y);
  y += 8;

  const section = (title: string) => {
    y = ensureSpace(doc, y, 14);
    doc.setFillColor(C.softBg.r, C.softBg.g, C.softBg.b);
    doc.roundedRect(M, y, CONTENT_W, 9, 1.2, 1.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(C.deep.r, C.deep.g, C.deep.b);
    doc.text(title.toUpperCase(), M + 3, y + 6.2);
    doc.setFont('helvetica', 'normal');
    y += 13;
  };

  section('Dados do comprador (NF-e e garantia)');
  doc.setFontSize(9.5);
  doc.setTextColor(30, 35, 45);
  const row = (label: string, value: string, extraLines = 0) => {
    y = ensureSpace(doc, y, 6 + extraLines * 4.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(C.muted.r, C.muted.g, C.muted.b);
    doc.setFontSize(7.5);
    doc.text(label, M, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(25, 30, 40);
    const vx = M + 38;
    const w = PAGE_W - M - vx;
    const chunks = doc.splitTextToSize(value, w);
    doc.text(chunks, vx, y);
    y += Math.max(5, 4.5 * chunks.length);
  };
  row('Nome completo', cliente.nome);
  row('Telefone / WhatsApp', cliente.telefone);
  row('CPF', cliente.cpf);
  row('Setor / área de trabalho', cliente.setor?.trim() || '—');
  row('Endereço de entrega', cliente.endereco, 1);
  y += 2;

  doc.setFillColor(240, 253, 250);
  doc.roundedRect(M, y, CONTENT_W, 11, 1.2, 1.2, 'F');
  doc.setDrawColor(C.accent.r, C.accent.g, C.accent.b);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, y, CONTENT_W, 11, 1.2, 1.2, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(15, 80, 72);
  doc.text('Preferência de pagamento', M + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const prefChunks = doc.splitTextToSize(linhaPreferenciaPagamento(preferenciaPagamento), CONTENT_W - 6);
  doc.text(prefChunks, M + 3, y + 8.5);
  y += 11 + (prefChunks.length - 1) * 3.8 + 6;

  section('Itens da encomenda');
  const xDesc = M;
  const xQty = M + 92;
  const xUnit = M + 108;
  const xSub = PAGE_W - M;

  y = ensureSpace(doc, y, 12);
  doc.setFillColor(235, 238, 242);
  doc.rect(M, y, CONTENT_W, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(55, 65, 80);
  doc.text('DESCRIÇÃO', xDesc + 2, y + 5.5);
  doc.text('QTD', xQty, y + 5.5, { align: 'center' });
  doc.text('UNIT. PROMO', xUnit + 24, y + 5.5, { align: 'right' });
  doc.text('SUBTOTAL', xSub - 2, y + 5.5, { align: 'right' });
  y += 9;
  doc.setDrawColor(C.line.r, C.line.g, C.line.b);
  doc.line(M, y, PAGE_W - M, y);
  y += 4;

  doc.setFont('helvetica', 'normal');
  for (const l of lines) {
    const nomeChunks = doc.splitTextToSize(l.nome, 88);
    const rowH = Math.max(10, 5 + (nomeChunks.length - 1) * 4);
    if (y + rowH + 8 > FOOTER_Y - 8) {
      doc.addPage();
      y = drawPageHeader(doc, 0, true);
      section('Itens da encomenda (continuação)');
    }
    doc.setFontSize(9);
    doc.setTextColor(25, 30, 40);
    doc.text(nomeChunks, xDesc + 1, y + 4);
    doc.setFontSize(7.5);
    doc.setTextColor(C.muted.r, C.muted.g, C.muted.b);
    doc.text(
      `Ref. tabela R$ ${fmtBRL(l.precoUnitCheio)}  ·  Preço promocional desta ação`,
      xDesc + 1,
      y + 4 + (nomeChunks.length - 1) * 4 + 3.5
    );
    doc.setFontSize(9);
    doc.setTextColor(25, 30, 40);
    doc.text(String(l.quantidade), xQty + 6, y + 4, { align: 'center' });
    doc.text(fmtBRL(l.precoUnitComDesconto), xUnit + 24, y + 4, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(fmtBRL(l.precoUnitComDesconto * l.quantidade), xSub - 2, y + 4, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    y += rowH + 2;
    doc.setDrawColor(238, 240, 244);
    doc.line(M, y, PAGE_W - M, y);
    y += 3;
  }

  y += 4;
  section('Resumo financeiro (estimado)');
  y += 2;

  const summaryRow = (label: string, value: string, opts?: { bold?: boolean }) => {
    y = ensureSpace(doc, y, 7);
    doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
    doc.setFontSize(opts?.bold ? 10.5 : 9);
    doc.setTextColor(45, 52, 65);
    doc.text(label, M + 2, y);
    doc.text(value, xSub - 2, y, { align: 'right' });
    y += opts?.bold ? 8 : 6;
  };

  summaryRow('Subtotal referência (preço de tabela)', `R$ ${fmtBRL(totais.subtotalCheio)}`);
  summaryRow('Subtotal com preços promocionais da ação', `R$ ${fmtBRL(totais.subtotalPrecosPromo)}`);
  if (totais.valorDescontoCombo > 0) {
    summaryRow('Combo especial (assento + pés ou plataforma)', `− R$ ${fmtBRL(totais.valorDescontoCombo)}`);
  }

  y += 2;
  doc.setFillColor(C.accent.r, C.accent.g, C.accent.b);
  doc.roundedRect(M, y, CONTENT_W, 12, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(C.night.r, C.night.g, C.night.b);
  doc.text('Total estimado a pagar', M + 4, y + 8);
  doc.text(`R$ ${fmtBRL(totais.subtotalComDesconto)}`, xSub - 4, y + 8, { align: 'right' });
  y += 18;

  y = ensureSpace(doc, y, 28);
  doc.setDrawColor(C.line.r, C.line.g, C.line.b);
  doc.line(M, y, PAGE_W - M, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(C.deep.r, C.deep.g, C.deep.b);
  doc.text('Próximos passos', M, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(C.muted.r, C.muted.g, C.muted.b);
  const nextLines = doc.splitTextToSize(
    '1) Enviar este resumo pelo WhatsApp ao Demétrio Jr. (67) 99857-6870 para confirmação.  2) A equipe alinha pagamento (PIX, cartão ou outra forma combinada — inclusive na entrega) e emite NF-e após acordo.  3) Este comprovante não substitui nota fiscal nem recibo de pagamento.',
    CONTENT_W
  );
  doc.text(nextLines, M, y);
  y += nextLines.length * 4 + 4;

  doc.setFontSize(7.5);
  doc.setTextColor(140, 145, 155);
  doc.text('comunicacao@lunins.org  ·  www.lunins.org  ·  @luninsbr', PAGE_W / 2, FOOTER_Y, { align: 'center' });
  doc.text('Documento gerado eletronicamente pelo cliente — sem valor fiscal.', PAGE_W / 2, FOOTER_Y + 4, { align: 'center' });

  return doc;
}

export function buildCassemsPedidoPdfFileName(clienteNome?: string): string {
  const nome = slugifyFilePart(clienteNome || 'cliente');
  return `lunins-encomenda-cassems-${nome || 'cliente'}.pdf`;
}

export function createCassemsPedidoPdfArtifact(params: Parameters<typeof buildCassemsPedidoPdf>[0]) {
  const doc = buildCassemsPedidoPdf(params);
  const fileName = buildCassemsPedidoPdfFileName(params.cliente.nome);
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.split('base64,')[1] ?? '';
  return { doc, fileName, base64 };
}

export function downloadCassemsPedidoPdf(
  params: Parameters<typeof buildCassemsPedidoPdf>[0],
  filename = buildCassemsPedidoPdfFileName(params.cliente.nome)
) {
  const { doc } = createCassemsPedidoPdfArtifact(params);
  doc.save(filename);
  return filename;
}
